import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import SpaState from '../states/spa.state';
import { deleteSpa, deregisterSpaUser, editSpa, editSpaModel, fetchSpa, flagSpa, registerSpaUser } from '../actions/spa.actions';
import { addSpaCommands } from '../actions/commands.actions';

const initialState: SpaState = {
    loading: false,
    isConnected: false,
    currentSpaId: null,          // Track which spa is being viewed
    isCurrentSpaConnected: false, // Connection status for current spa
    isConnecting: false,
    data: [],
    error: null,
};

const spaSlice = createSlice({
    name: 'spa',
    initialState,
    reducers: {
        setCurrentSpa: (state, action: PayloadAction<string>) => {
            // Called when switching to a new spa
            state.currentSpaId = action.payload;
            state.isCurrentSpaConnected = false; // Reset connection status
            state.isConnecting = true;
        },
        setConnected: (state, action: PayloadAction<string>) => {
            // Only update if the connected spa matches current view
            if (state.currentSpaId === action.payload) {
                state.isCurrentSpaConnected = true;
                state.isConnecting = false;
            }
        },
        setDisconnected: (state, action: PayloadAction<string>) => {
            if (state.currentSpaId === action.payload) {
                state.isCurrentSpaConnected = false;
                state.isConnecting = false;
            }
        },
        resetConnection: (state) => {
            // Full reset
            state.isCurrentSpaConnected = false;
            state.isConnecting = false;
            state.currentSpaId = null;
        },
        setConnecting: (state, action: PayloadAction<string>) => {
            if (state.currentSpaId === action.payload) {
                state.isConnecting = true;
            }
        },
        setConnectingFinished: (state, action: PayloadAction<string>) => {
            if (state.currentSpaId === action.payload) {
                state.isConnecting = false;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpa.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSpa.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchSpa.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            .addCase(editSpa.pending, (state) => {
                state.error = null;
            })
            .addCase(editSpa.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.data = {...state.data,nick_name: action.payload.nick_name, dealership_id: action.payload.dealership_id, arctic_serial_number: action.payload.arctic_serial_number};
            })
            .addCase(editSpa.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            .addCase(registerSpaUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerSpaUser.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.data = {...state.data,user_id: action.payload.user_id, user:action.payload.user};
            })
            .addCase(registerSpaUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            .addCase(deregisterSpaUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deregisterSpaUser.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.data = {...state.data,user_id: action.payload.user_id, user:action.payload.user};
            })
            .addCase(deregisterSpaUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })
            
            .addCase(addSpaCommands.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addSpaCommands.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
            })
            .addCase(addSpaCommands.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            .addCase(deleteSpa.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSpa.fulfilled, (state) => {
                state.loading = false;
                state.data = null;
            })
            .addCase(deleteSpa.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            .addCase(flagSpa.pending, (state) => {
                state.error = null;
            })
            .addCase(flagSpa.fulfilled, (state,action: PayloadAction<any>) => {
                state.data = action.payload;
            })
            .addCase(flagSpa.rejected, (state, action) => {
                state.error = action.payload ?? null;
            })

            .addCase(editSpaModel.pending, (state) => {
                state.error = null;
            })
            .addCase(editSpaModel.fulfilled, (state, action: PayloadAction<any>) => {
                state.data = {...state.data,tub_model_id: action.payload.tub_model_id,tub_model:action.payload.tub_model};
            })
            .addCase(editSpaModel.rejected, (state, action) => {
                state.error = action.payload ?? null;
            });
    },
});

export const {
    setCurrentSpa, 
    setConnected, 
    setDisconnected, 
    resetConnection,
    setConnecting,
    setConnectingFinished
} = spaSlice.actions;
export default spaSlice.reducer;